import type {
  EvalsRecordEvalRequest,
  EvalsRecordEvalResponse,
  EvalsScoreFeatureRequest,
  EvalsScoreFeatureResponse,
} from '@chronosphereio/core';
import { getEnvVar } from '../utils';

/**
 * Client for calling the evals backend API.
 * Supports both scoring features using LLM judges and recording eval results.
 *
 * @param baseUrl - Base URL for the evals API. Use empty string '' for browser (relative URLs),
 *                  or full URL for Node.js environments.
 */
export class EvalsClient {
  constructor(private readonly baseUrl: string = '') {}

  /**
   * Build headers for API requests.
   * Includes API-Token header only when baseUrl is provided (Node.js context).
   * Browser context uses cookies/session for auth, so no token needed.
   */
  private getHeaders(): HeadersInit {
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
    };

    // Only add API-Token for non-browser contexts (when baseUrl is provided)
    if (this.baseUrl) {
      const token = getEnvVar('CHRONOSPHERE_API_TOKEN', { required: false });
      if (token) {
        headers['API-Token'] = token;
      }
    }

    return headers;
  }

  async scoreFeature(req: EvalsScoreFeatureRequest): Promise<EvalsScoreFeatureResponse> {
    const response = await fetch(`${this.baseUrl}/api/evals/score`, {
      method: 'POST',
      headers: this.getHeaders(),
      body: JSON.stringify(req),
    });

    if (!response.ok) {
      const text = await response.text();
      throw new Error(`Evals backend request failed: ${response.status} ${response.statusText}. Response: ${text}`);
    }

    const data = await response.json();
    if (!data) {
      throw new Error('Evals backend returned empty response');
    }

    return data;
  }

  async recordEval(req: EvalsRecordEvalRequest): Promise<EvalsRecordEvalResponse> {
    const response = await fetch(`${this.baseUrl}/api/evals/record`, {
      method: 'POST',
      headers: this.getHeaders(),
      body: JSON.stringify(req),
    });

    if (!response.ok) {
      const text = await response.text();
      throw new Error(`Evals backend request failed: ${response.status} ${response.statusText}. Response: ${text}`);
    }

    const data = await response.json();
    if (!data) {
      throw new Error('Evals backend returned empty response');
    }

    return data;
  }
}
